import { Component } from '@theme/component';
import { StandardEvents } from '@shopify/events';

class ColopCustomizer extends Component {
  #hasDesign = false;
  #sectionId = '';
  #productFormId = '';
  #designRequiredText = '';
  #addToCartText = '';
  #previewAltText = '';

  /** @type {{designId: string, permalinkId: string, templateId: string, padColor: string, previewUrl: string} | null} */
  #lastDesignData = null;

  /** @type {MutationObserver | null} */
  #buttonObserver = null;

  // Bound handlers stored once so removeEventListener works
  #boundHandleDesignCreated = this.#handleDesignCreated.bind(this);
  #boundHandleCartUpdate = this.#handleCartUpdate.bind(this);
  #boundHandleVariantUpdate = this.#handleVariantUpdate.bind(this);
  #boundHandleFormData = this.#handleFormData.bind(this);

  connectedCallback() {
    super.connectedCallback();

    this.#sectionId = this.dataset.sectionId || '';
    this.#productFormId = this.dataset.productFormId || '';
    this.#designRequiredText = this.dataset.designRequiredText || 'Design and save to add to cart';
    this.#addToCartText = this.dataset.addToCartText || 'Add to cart';
    this.#previewAltText = this.dataset.previewAltText || 'Custom stamp design preview';

    document.body.addEventListener('creator6cartdata', this.#boundHandleDesignCreated);
    document.addEventListener(StandardEvents.cartLinesUpdate, this.#boundHandleCartUpdate);
    document.addEventListener(StandardEvents.productSelect, this.#boundHandleVariantUpdate);

    // Primary mechanism: append properties when product-form.js builds `new FormData(form)`.
    // `formdata` bubbles and fires at FormData construction, so this lands the properties
    // regardless of morph timing or the document-capture submit delegation in component.js.
    document.addEventListener('formdata', this.#boundHandleFormData);

    // The button's disabled state is the only real gate: component.js delegates
    // `submit` at document level with capture, so `#processAddToCart` (and its fetch)
    // runs synchronously before any section-level listener could block it.
    // The observer guarantees the button can never be enabled without a design.
    this.#disableAddToCartButtons();
    this.#startButtonObserver();
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    document.body.removeEventListener('creator6cartdata', this.#boundHandleDesignCreated);
    document.removeEventListener(StandardEvents.cartLinesUpdate, this.#boundHandleCartUpdate);
    document.removeEventListener(StandardEvents.productSelect, this.#boundHandleVariantUpdate);
    document.removeEventListener('formdata', this.#boundHandleFormData);

    this.#buttonObserver?.disconnect();
    this.#buttonObserver = null;
  }

  /**
   * Handles the design created event from the Colop Creator6 widget.
   * @param {Event} event
   */
  #handleDesignCreated(event) {
    const data = typeof /** @type {CustomEvent} */ (event).detail === 'string'
      ? JSON.parse(/** @type {CustomEvent} */ (event).detail)
      : /** @type {CustomEvent} */ (event).detail;

    const designId = data.renderingId || data.docId || '';
    const permalinkId = data.permalinkId || '';
    const previewUrl = data.previewImageUrl ||
      (permalinkId ? `https://inchbug-c6i.colop.com/preview/${permalinkId}` : '');

    this.#lastDesignData = {
      designId,
      permalinkId,
      templateId: data.templateId || '',
      padColor: data.padColor || '',
      previewUrl,
    };
    this.#hasDesign = true;

    if (previewUrl) {
      this.#updatePreviewImages(previewUrl);
    }

    // Show preview overlay on the media gallery
    this.#showPreviewOverlay(previewUrl);

    // Inject properties into form and enable the button
    this.#injectProperties();
    this.#enableAddToCartButtons();
  }

  /**
   * Appends the Colop line item properties to the FormData being built for the
   * product add. Fires on the form's `formdata` event during `new FormData(form)`
   * (product-form.js line ~392), so the properties land even when a morph has wiped
   * the hidden inputs and regardless of capture-phase ordering. Skips when the hidden
   * inputs already cover it, to avoid duplicate properties.
   *
   * @param {FormDataEvent} event
   */
  #handleFormData(event) {
    const form = /** @type {HTMLElement} */ (event.target);
    if (!this.#isProductForm(form)) return;
    if (!this.#hasDesign || !this.#lastDesignData) return;

    const fd = event.formData;
    if (fd.has('properties[_Design ID]')) return; // hidden inputs already present

    const d = this.#lastDesignData;
    fd.append('properties[_Design ID]', d.designId);
    fd.append('properties[_Permalink ID]', d.permalinkId);
    fd.append('properties[_Template ID]', d.templateId);
    fd.append('properties[_Pad Color]', d.padColor);
    fd.append('properties[Preview URL]', d.previewUrl);
  }

  /**
   * Handles variant update events. Unchecks "I confirm design" in the
   * Colop widget to force the user to re-confirm after switching color,
   * which also prevents add-to-cart without valid properties.
   *
   * @param {Event} event
   */
  #handleVariantUpdate(event) {
    // Uncheck the "I confirm design" checkbox in the Creator6 widget
    this.#uncheckConfirmDesign();

    // Clear design state — user must re-confirm via the widget
    this.#hasDesign = false;
    this.#removePreviewOverlay();

    // Remove properties from form
    const form = this.#findProductForm();
    if (form) {
      const container = form.querySelector('#colop-properties-container');
      if (container) container.remove();
    }

    // Disable now; the button observer keeps it disabled when product-form.js
    // re-enables it after the variant-change section render resolves.
    this.#disableAddToCartButtons();
  }

  /**
   * Unchecks the "I confirm design" checkbox inside the Colop Creator6 widget.
   */
  #uncheckConfirmDesign() {
    const widget = this.closest('.shopify-section')?.querySelector('.c6ui');
    if (!widget) return;

    // Creator6 uses a checkbox input for "I confirm the design"
    const checkboxes = widget.querySelectorAll('input[type="checkbox"]');
    for (const cb of checkboxes) {
      if (cb instanceof HTMLInputElement && cb.checked) {
        cb.click();
      }
    }
  }

  /**
   * Watches the section for add-to-cart buttons becoming enabled without a
   * confirmed design and disables them again immediately. This is what closes
   * the variant-change race: product-form.js calls `enable()` on the button
   * whenever the section-render fetch resolves (product-form.js #onProductSelect),
   * which can land at any time — after any fixed timer. Observing `disabled`
   * flips and morph node swaps is deterministic regardless of network timing.
   * The observer callback runs as a microtask, so a re-disable always lands
   * before the next user click event can be processed.
   */
  #startButtonObserver() {
    const section = this.closest('.shopify-section');
    if (!section) return;

    this.#buttonObserver = new MutationObserver(() => {
      if (this.#hasDesign) return;

      // Only act when an enabled button exists — our own disable/text updates
      // also trigger the observer, and this guard stops that feedback loop.
      const hasEnabledButton = this.#findAddToCartButtons().some((button) => !button.disabled);
      if (!hasEnabledButton) return;

      this.#disableAddToCartButtons();
    });

    // Subtree-wide: catches both `disabled` attribute flips on existing buttons
    // and morphs that swap in fresh (enabled) button nodes.
    this.#buttonObserver.observe(section, {
      subtree: true,
      childList: true,
      attributes: true,
      attributeFilter: ['disabled'],
    });
  }

  /**
   * Handles cart update events. Resets design state after a successful add.
   * @param {Event} event
   */
  #handleCartUpdate(event) {
    // StandardEvents.cartLinesUpdate (CartLinesUpdateEvent): reset the design after a
    // successful add so the next stamp requires a fresh design.
    const e = /** @type {any} */ (event);
    if (e.action !== 'add' || !this.#hasDesign) return;
    e.promise
      ?.then(({ detail }) => {
        if (!detail?.didError) this.#resetDesignState();
      })
      .catch(() => {});
  }

  /**
   * Injects the Colop line item properties into the product form.
   * Safe to call repeatedly — removes any existing container first.
   */
  #injectProperties() {
    if (!this.#lastDesignData) return;

    const form = this.#findProductForm();
    if (!form) return;

    const existing = form.querySelector('#colop-properties-container');
    if (existing) existing.remove();

    const container = document.createElement('div');
    container.id = 'colop-properties-container';

    const fields = [
      ['_Design ID', this.#lastDesignData.designId],
      ['_Permalink ID', this.#lastDesignData.permalinkId],
      ['_Template ID', this.#lastDesignData.templateId],
      ['_Pad Color', this.#lastDesignData.padColor],
      ['Preview URL', this.#lastDesignData.previewUrl],
    ];

    for (const [name, value] of fields) {
      const input = document.createElement('input');
      input.type = 'hidden';
      input.name = `properties[${name}]`;
      input.value = value;
      container.appendChild(input);
    }

    form.appendChild(container);
  }

  #resetDesignState() {
    this.#hasDesign = false;
    this.#lastDesignData = null;
    this.#removePreviewOverlay();

    const form = this.#findProductForm();
    if (form) {
      const container = form.querySelector('#colop-properties-container');
      if (container) container.remove();
    }

    this.#disableAddToCartButtons();
  }

  /**
   * Finds the product form element.
   * @returns {HTMLFormElement | null}
   */
  #findProductForm() {
    /** @type {HTMLFormElement | null} */
    let form = document.querySelector(`form#${this.#productFormId}`);
    if (form) return form;

    form = document.querySelector('form[data-type="add-to-cart-form"]');
    if (form) return form;

    const productFormComponent = document.querySelector('product-form-component');
    if (productFormComponent) {
      form = productFormComponent.querySelector('form');
    }
    return form;
  }

  /**
   * Checks whether the given element is the product form we care about.
   * @param {HTMLElement} el
   * @returns {boolean}
   */
  #isProductForm(el) {
    if (!(el instanceof HTMLFormElement)) return false;
    if (el.id === this.#productFormId) return true;
    if (el.dataset.type === 'add-to-cart-form') return true;
    if (el.closest('product-form-component')) return true;
    return false;
  }

  /**
   * Shows a preview overlay image on the product media gallery.
   * @param {string} previewUrl
   */
  #showPreviewOverlay(previewUrl) {
    if (!previewUrl) return;

    this.#removePreviewOverlay();

    const section = this.closest('.shopify-section');
    const mediaContainer = section?.querySelector('.product-media-container')
      || section?.querySelector('.product-information__media');
    if (!mediaContainer) return;

    const overlay = document.createElement('div');
    overlay.id = 'colop-preview-overlay';
    overlay.className = 'colop-preview-overlay';

    const img = document.createElement('img');
    img.src = previewUrl;
    img.alt = this.#previewAltText;
    img.className = 'colop-preview-overlay__image';

    overlay.appendChild(img);
    mediaContainer.appendChild(overlay);
  }

  /**
   * Removes the preview overlay from the product media gallery.
   */
  #removePreviewOverlay() {
    const overlay = this.closest('.shopify-section')
      ?.querySelector('#colop-preview-overlay');
    if (overlay) overlay.remove();
  }

  #updatePreviewImages(previewUrl) {
    const selectors = [
      '.product-media-container .product-media-gallery__item',
      '.product-media-gallery__item',
      '.product-media-container img',
      '[data-product-media-gallery] img',
    ];

    for (const selector of selectors) {
      const images = document.querySelectorAll(selector);
      if (images.length > 0) {
        const firstImage = images[0];
        if (firstImage instanceof HTMLImageElement) {
          firstImage.src = previewUrl;
          firstImage.alt = this.#previewAltText;
          break;
        }
      }
    }
  }

  #disableAddToCartButtons() {
    for (const button of this.#findAddToCartButtons()) {
      button.disabled = true;
      this.#updateButtonText(button, this.#designRequiredText);
    }
  }

  #enableAddToCartButtons() {
    for (const button of this.#findAddToCartButtons()) {
      button.disabled = false;
      this.#updateButtonText(button, this.#addToCartText);
    }
  }

  /**
   * @returns {HTMLButtonElement[]}
   */
  #findAddToCartButtons() {
    /** @type {HTMLButtonElement[]} */
    const buttons = [];
    const selectors = [
      `form#${this.#productFormId} button[type="submit"]`,
      `form#${this.#productFormId} button[name="add"]`,
      'form[data-type="add-to-cart-form"] button[type="submit"]',
      'form[data-type="add-to-cart-form"] button[name="add"]',
      '[data-testid="colop-add-to-cart"]',
    ];

    for (const selector of selectors) {
      for (const btn of document.querySelectorAll(selector)) {
        if (btn instanceof HTMLButtonElement && !buttons.includes(btn)) {
          buttons.push(btn);
        }
      }
    }

    return buttons;
  }

  /**
   * @param {HTMLButtonElement} button
   * @param {string} text
   */
  #updateButtonText(button, text) {
    const textSpan = button.querySelector('.add-to-cart-text__content span span');
    if (textSpan) {
      textSpan.textContent = text;
    } else {
      const contentSpan = button.querySelector('.add-to-cart-text__content');
      if (contentSpan) {
        contentSpan.innerHTML = `<span>${text}</span>`;
      }
    }
  }
}

if (!customElements.get('colop-customizer')) {
  customElements.define('colop-customizer', ColopCustomizer);
}
